/**
 * Кадр исходника в квадрат 512×512 JPEG — тот же формат, что кладёт
 * `AvatarEditor` на витрине. Онбординг кропает по центру: отдельного окна
 * кадрирования на первом шаге нет, а сырой 8–12 МБ файл сервер отвергает
 * ещё до PUT (`MAX_AVATAR_BYTES` = 5 МБ).
 */
export const AVATAR_OUTPUT_SIZE = 512;
export const AVATAR_JPEG_QUALITY = 0.9;
/** Потолок исходника до сжатия. Итог после кропа обязан уложиться в 5 МБ. */
export const AVATAR_MAX_SOURCE_BYTES = 12 * 1024 * 1024;

export type AvatarPrepareReason = "size" | "decode";

export class AvatarPrepareError extends Error {
	constructor(readonly reason: AvatarPrepareReason) {
		super(reason);
		this.name = "AvatarPrepareError";
	}
}

export async function prepareAvatarJpeg(file: File): Promise<Blob> {
	if (file.size > AVATAR_MAX_SOURCE_BYTES) {
		throw new AvatarPrepareError("size");
	}

	const objectUrl = URL.createObjectURL(file);
	try {
		const image = await loadImage(objectUrl);
		return await renderCenterSquare(image);
	} catch (error) {
		if (error instanceof AvatarPrepareError) throw error;
		throw new AvatarPrepareError("decode");
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}

function loadImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error("decode failed"));
		image.src = url;
	});
}

function renderCenterSquare(image: HTMLImageElement): Promise<Blob> {
	const canvas = document.createElement("canvas");
	canvas.width = AVATAR_OUTPUT_SIZE;
	canvas.height = AVATAR_OUTPUT_SIZE;
	const context = canvas.getContext("2d");
	if (!context) throw new AvatarPrepareError("decode");

	// JPEG альфы не знает — без заливки PNG приедет с чёрными углами.
	context.fillStyle = "#ffffff";
	context.fillRect(0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);

	const side = Math.min(image.naturalWidth, image.naturalHeight);
	const sx = (image.naturalWidth - side) / 2;
	const sy = (image.naturalHeight - side) / 2;
	context.drawImage(image, sx, sy, side, side, 0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);

	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => (blob ? resolve(blob) : reject(new AvatarPrepareError("decode"))),
			"image/jpeg",
			AVATAR_JPEG_QUALITY,
		);
	});
}
